import { mapState, mapGetters, mapActions } from 'vuex';
import select from 'dom-select';
import {
  formatPrice,
  handleize,
  decodeIDvariant,
  htmlDecode,
} from 'utils/general';
import * as gqlQueries from 'lib/graphql/queries';
import { trackEvent } from 'lib/tracking';

export default {
  props: ['prefix'],
  data() {
    return {
      donateProduct: {},
      recommendedProducts: [],
      asyncDetailsFetched: false,
      donateVariantId: null,
      token: '',
      tooltipActive: false,
      loadingRecommendations: false,
    };
  },
  computed: {
    ...mapState('global', ['cart', 'activeCountryCode']),
    ...mapState('cart', ['loading', 'isBootstrapped', 'open', 'productsData']),
    ...mapGetters('global', ['cartHasGWPProducts', 'GWPProducts']),
    ...mapGetters('cart', ['subtotal']),
    isActive() {
      return this.open;
    },
    cartStatus() {
      return this.open;
    },
    cartItems() {
      return (
        this.cart.items.map((item) => {
          // TODO: check against tracking needs
          if (!this.isProbablyBundled(item)) {
            if (
              this.productsData[item.handle] &&
              this.productsData[item.handle].variants
            ) {
              const cartVariant = this.productsData[item.handle].variants.find(
                (variant) =>
                  decodeIDvariant(variant.node.id) ===
                  item.variant_id.toString(),
              );
              let data = {
                ...item,
                  tags: this.productsData[item.handle].tags,
                  reviewsAverage: item.properties?._product_rating || undefined,
                  type: item.product_type,
                  available: true,
              }
              if (cartVariant && cartVariant.node.compareAtPrice) {
                data = {
                  ...data,
                  compare_at_price:
                    parseFloat(cartVariant.node.compareAtPrice.amount) * 100,
                }
              }

              return data;
            }
          }
          if (this.GWPProducts) {
            const itemWithCollectionPromo = this.GWPProducts.find(
              (el) => parseInt(el.id) === parseInt(item.product_id),
            );
            if (itemWithCollectionPromo) {
              return {
                ...item,
                hasCollectionPromo: true,
              };
            }
          }
          return item;
        }) || []
      );
    },
    bundledItems() {
      const bundles = {};
      this.cartItems.forEach((item) => {
        if (this.isProbablyBundled(item)) {
          const bundleUid = item.properties?._bundle_cart_uid ?? null;

          if (typeof bundles[bundleUid] === 'undefined') {
            const productId = parseInt(bundleUid.split('_')[0]);
            bundles[bundleUid] = {
              ...item,
              items: [],
              isBundle: true,
              id: bundleUid,
              properties: item.properties,
              product_title:
                item.properties?._bundle_title ??
                htmlDecode(item.product_title) ??
                null,
              price: 0,
              compare_at_price: 0,
              bundle_compare_price: 0,
              line_price: 0,
              product_id: productId,
              tags: this.productsData[item.handle]?.tags ?? [],
              reviewsAverage: item.properties?._product_rating || undefined,
              type: item.product_type,
              available: true,
            };
          }
          bundles[bundleUid].image = item.properties._bundle_image;
          bundles[bundleUid].compare_at_price += item.price;
          bundles[bundleUid].bundle_compare_price =
            item.properties._bundle_compare_price;
          bundles[bundleUid].price += item.final_price;
          bundles[bundleUid].line_price += item.final_price * item.quantity;
          bundles[bundleUid].items.push(item);
        }
      });

      Object.keys(bundles).forEach((bundleId) => {
        bundles[bundleId].items = bundles[bundleId].items.sort(
          (a, b) =>
            parseInt(a.properties?._bundle_index ?? 0) -
            parseInt(b.properties?._bundle_index ?? 0),
        );
      });

      return bundles;
    },
    updatedCartItems() {
      const items = [
        ...Object.values(this.bundledItems),
        ...this.cartItems.filter((i) => !this.isProbablyBundled(i)),
      ];
      return items;
    },

    donateItems() {
      const hola = [...this.cartItems.filter((i) => this.isDonateItem(i))];
      return hola;
    },

    cartTotal() {
      setTimeout(() => {
        if (typeof Afterpay !== 'undefined') {
          select
            .all('.header-cart__footer__afterpay afterpay-placement')
            .forEach((item) => {
              item.remove();
            });
          select
            .all('.cart-grid__checkout-footer afterpay-placement')
            .forEach((item) => {
              item.remove();
            });
          select
            .all('.cart-checkout-mobile__footer afterpay-placement')
            .forEach((item) => {
              item.remove();
            });

          Afterpay.createPlacements({
            targetSelector: '.header-cart__footer__afterpay-prefix',
            attributes: {
              currency: 'USD',
              locale: 'en_US',
              amount: parseFloat(this.cart.total_price / 100),
            },
          });

          Afterpay.createPlacements({
            targetSelector: '.cart-grid__checkout-footer-afterpay',
            attributes: {
              currency: 'USD',
              locale: 'en_US',
              amount: parseFloat(this.cart.total_price / 100),
            },
          });

          Afterpay.createPlacements({
            targetSelector: '.cart-checkout-mobile__footer-afterpay',
            attributes: {
              currency: 'USD',
              locale: 'en_US',
              amount: parseFloat(this.cart.total_price / 100),
            },
          });
        }
      }, 1000);

      return formatPrice(this.cart.total_price, true, true);
    },
    discountTotal() {
      const savings = this.cartItems.reduce((acc, rec) => {
        if (
          !rec.properties._bundle &&
          rec.compare_at_price &&
          rec.compare_at_price > rec.price
        ) {
          acc += (rec.compare_at_price - rec.price) * rec.quantity;
        }
        return acc;
      }, 0);

      if (this.cart.total_discount + savings === 0) {
        return 0;
      } else {
        return formatPrice(this.cart.total_discount + savings, false, true);
      }
    },
    itemCountText() {
      const count = this.cartItems.reduce((total, item) => {
        total += item.quantity;
        return total;
      }, 0);

      return count > 0 ? `(${count})` : '';
    },
    isEmpty() {
      return this.cartItems.length === 0;
    },
    installmentPaymentsText() {
      const payment_count = window.location.hostname.split(`.`).pop() === `uk` ? 3 : 4;
      const text = this.$t('installment_payments.text_content');
      const price = this.cart.total_price;
      const formattedPrice = formatPrice(Math.floor(price / payment_count));
      return text.replace('{payment_count}', payment_count).replace('{price}', formattedPrice);
    },
    donateId() {
      return __GLOBAL__.donateProduct.productId ?? null;
    },
    defaultDonateVariantId() {
      return __GLOBAL__.donateProduct.variantId ?? null;
    },
  },
  methods: {
    ...mapActions('cart', ['addToCart']),
    ...mapActions('global', ['requestRewardsCollection']),
    isBeautyItem(item) {
      return item.vendor === 'Fenty Beauty' && !this.isDonateItem(item);
    },
    isSkinItem(item) {
      return item.vendor === 'Fenty Skin' && !this.isDonateItem(item);
    },
    isDonateItem(item) {
      return (
        this.donateId && item.product_id.toString() === this.donateId.toString()
      );
    },
    isProbablyBundled(item) {
      return (
        ((item.properties?._bundle_uid ?? false) &&
          (item.properties?._bundle_validated ?? false)) ||
        ((item.properties?._bundle_uid ?? false) &&
          this.loading === 'cartBundles')
      );
    },
    getVendorItemsLength(vendor) {
      let length = 0;
      if (!this[vendor]) {
        return 0;
      }
      this[vendor].forEach((element) => {
        if (element.isBundle) {
          length += element.items.length * element.quantity;
        } else {
          length += 1 * element.quantity;
        }
      });
      return length;
    },
    priceFormat(price) {
      return formatPrice(price, false, true);
    },
    priceFormatCartDonation(price) {
      return window.Shopify.currency.active + ' ' + formatPrice(price, false,);
    },
    onChangeDonate(donateVariantId) {
      this.donateVariantId = donateVariantId;
    },
    onAddDonateToCart() {
      if (!this.donateVariantId) {
        this.donateVariantId = this.defaultDonateVariantId.toString();
      }
      const option = this.$refs.select.querySelector(
        `option[value="${this.donateVariantId}"]`,
      );
      if (option) {
        trackEvent({
          event: 'donation_added',
          donation_value: option.textContent.trim(),
        });
      }
      this.addToCart({
        id: this.donateVariantId,
        quantity: 1,
        properties: {
          _origin: document.body.dataset.origin,
          _origin_product: document.body.dataset.originProduct,
          _item_list_id: 'donation',
          _item_list_name: 'Donation',
          _index: 0,
        },
      });
      this.donateVariantId = this.defaultDonateVariantId.toString();
    },
    onRemove(cartRemoveFunction) {
      this.cartRemoveFunction = cartRemoveFunction;
      this.cartRemoveFunction();
    },
    requestAsyncDetails() {
      this.loadingRecommendations = true;
      fetch(`${Shopify.routes.root}?view=cart`)
        .then((response) => {
          return response.json();
        })
        .then(({ donateProduct = {}, recommendedProducts = [] } = {}) => {
          this.asyncDetailsFetched = true;
          this.recommendedProducts = recommendedProducts;
          this.loadingRecommendations = false;
          this.donateProduct = donateProduct;
        });
    },
    async requestAsyncToken() {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      const { token } = await response.json();
      this.token = token;
    },
    showTooltipContent() {
      this.tooltipActive = true;
    },
    hideTooltipContent() {
      this.tooltipActive = false;
    },
    checkFocus(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.hideTooltipContent();
      }
    },
  },
};
